import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Permission } from '@/models/Permission';
import { Patient } from '@/models/Patient';

/**
 * Single source of truth for the remote-worker permission code and the checks that
 * mirror the backend RemoteWorkHelper rule. Screens use these to disable editing up
 * front for remote workers in blocked states, instead of letting them do data entry
 * that the API would ultimately reject.
 */
export const RemoteWorkerPermissionCode = 'Other/Remote Worker';

/**
 * True when the state appears in the given configured list. Matching is trimmed and
 * case-insensitive on both sides, mirroring the backend's normalized comparison, so config
 * or state values like "oh" or "OH " still match. Module-scoped rather than a class member so
 * it stays a plain function instead of being merged into the component's methods.
 */
function stateIsIn(state: string, configuredStates?: string[] | null): boolean {
    const normalized = state.trim().toUpperCase();
    return (configuredStates ?? [])
        .map(s => (s ?? '').trim().toUpperCase())
        .includes(normalized);
}

@Component
export default class RemoteWorkerMixin extends Vue {
    /** True when the current user holds the remote-worker permission. */
    get isRemoteWorker(): boolean {
        return this.$permissions.some(
            (p: Permission) => p.permissionCodeName === RemoteWorkerPermissionCode
        );
    }

    /**
     * Mirrors the backend's combined rule (RemoteWorkHelper.CannotAccessPatient): a remote worker
     * is blocked from clinical work on a patient residing in either RemoteDisabledStates or the
     * read-only StateRestrictedStates. Both lists matter — the write gates call the combined check,
     * so a state configured only as read-only rejects the save just the same, and checking only the
     * first list would leave the UI editable right up until the API refused it.
     * Returns false for non-remote-workers or a blank state.
     */
    isRemoteWorkBlockedForState(state?: string | null): boolean {
        if (!this.isRemoteWorker || !state) return false;
        return stateIsIn(state, this.$remoteDisabledStates)
            || stateIsIn(state, this.$stateRestrictedStates);
    }

    /**
     * Mirrors the backend's Patient.PrimaryAddress (RemoteWorkHelper checks PrimaryAddress.State):
     * the primary address, else the first address.
     */
    primaryAddressStateOf(patient?: Patient | null): string | undefined {
        const addresses = patient?.addresses ?? [];
        return (addresses.find(a => a.isPrimary) ?? addresses[0])?.state;
    }

    /**
     * Convenience wrapper over isRemoteWorkBlockedForState for screens that hold a whole patient
     * rather than a state string. Callers that need to freeze the state at load time (so editing
     * the address can't trap the user mid-form) should snapshot primaryAddressStateOf instead.
     */
    isRemoteWorkBlockedForPatient(patient?: Patient | null): boolean {
        return this.isRemoteWorkBlockedForState(this.primaryAddressStateOf(patient));
    }
}
